import React, { useState } from 'react';
import { Typography, Box, Card, Chip, Popover } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

const cx = (...classNames) => classNames.filter(Boolean).join(' ');
const VISIBLE_COUNT = 3;

const UpcomingFeaturesCard = ({ features }) => {
    const [isExpanded, setExpanded] = useState(false);
    const [previewAnchor, setPreviewAnchor] = useState(null);
    const canExpand = features.length > VISIBLE_COUNT;
    const primaryFeatures = features.slice(0, VISIBLE_COUNT);
    const extraFeatures = features.slice(VISIBLE_COUNT);
    const isPreviewOpen = Boolean(previewAnchor);

    const renderFeature = (feature, index) => {
        const [stageLabel, ...descriptionParts] = feature.status.split(':');
        const description = descriptionParts.join(':').trim();

        return (
            <Box key={feature.title} className={cx('whats-new-upcoming-feature', index === 0 && 'is-first')} style={{ '--feature-color': feature.color, '--feature-glow': `${feature.color}18` }}>
                <Box className="whats-new-upcoming-feature-row">
                    <Box className="whats-new-upcoming-feature-dot" />
                    <Box className="whats-new-upcoming-feature-text">
                        <Typography className="whats-new-upcoming-feature-title">{feature.title}</Typography>
                        <Typography className="whats-new-upcoming-feature-meta">{feature.application} · {feature.eta}</Typography>
                    </Box>
                </Box>
                <Box className="whats-new-upcoming-feature-stage">
                    <Box className="whats-new-upcoming-feature-stage-dot" />
                    {stageLabel}
                </Box>
                {description ? <Typography className="whats-new-upcoming-feature-status">{description}</Typography> : null}
            </Box>
        );
    };

    return (
        <Card elevation={0} className="whats-new-upcoming-card">
            <Box className="whats-new-upcoming-header">
                <Box className="whats-new-upcoming-header-row">
                    <AccessTimeRoundedIcon />
                    <Typography className="whats-new-upcoming-title">Coming up next</Typography>
                    <Chip
                        label="PREVIEW"
                        size="small"
                        clickable
                        className="whats-new-preview-chip"
                        onClick={(event) => setPreviewAnchor(event.currentTarget)}
                        aria-describedby={isPreviewOpen ? 'whats-new-preview-info' : undefined}
                        aria-expanded={isPreviewOpen}
                    />
                </Box>
                <Typography className="whats-new-upcoming-subtitle">A sneak peek at what our teams are building.</Typography>
            </Box>
            <Box className="whats-new-upcoming-body">
                <Box className="whats-new-upcoming-list">
                    {primaryFeatures.map(renderFeature)}
                    {canExpand ? (
                        <Box className={cx('whats-new-upcoming-list-extra', isExpanded && 'is-expanded')}>
                            <Box className="whats-new-upcoming-list-extra-inner">
                                {extraFeatures.map((feature, index) => renderFeature(feature, index + VISIBLE_COUNT))}
                            </Box>
                        </Box>
                    ) : null}
                </Box>
                {canExpand ? (
                    <Box
                        component="button"
                        type="button"
                        className={cx('whats-new-upcoming-footer', isExpanded && 'is-expanded')}
                        onClick={() => setExpanded(prev => !prev)}
                    >
                        {isExpanded ? <ArrowForwardRoundedIcon /> : null}
                        <Typography className="whats-new-upcoming-footer-label">
                            {isExpanded ? 'Show less' : 'Explore the roadmap'}
                        </Typography>
                        {isExpanded ? null : <ArrowForwardRoundedIcon />}
                    </Box>
                ) : null}
            </Box>
            <Popover
                id="whats-new-preview-info"
                open={isPreviewOpen}
                anchorEl={previewAnchor}
                onClose={() => setPreviewAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { className: 'whats-new-preview-popover' } }}
            >
                <Typography className="whats-new-preview-popover-title">Product roadmap preview</Typography>
                <Typography className="whats-new-preview-popover-copy">
                    Get an early look at features our product teams are planning, building or testing. Scope and target dates are estimates and may change before release.
                </Typography>
                <Box className="whats-new-preview-popover-stages">
                    <Typography><Box component="span" className="is-development" />In development</Typography>
                    <Typography><Box component="span" className="is-testing" />User testing</Typography>
                    <Typography><Box component="span" className="is-roadmap" />On the roadmap</Typography>
                </Box>
            </Popover>
        </Card>
    );
};

export default UpcomingFeaturesCard;
